import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { format } from 'date-fns';
import { Box, Dialog, IconButton, Typography } from '@mui/material';

import { CustomButton } from '../../../../components/CustomButton';
import { ReactComponent as CloseIcon } from '../../../../assets/images/close.svg';
import { CustomDatePicker } from '../../../../components/CustomDatePicker/CustomDatePicker';

import { requestExportCustomers } from '../../../../api/support';
import { PERMISSIONS } from '../../../../constants/variables';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';

const StyledDialog = styled(Dialog)(() => ({
  '& > .MuiDialog-container': {
    '& > .MuiDialog-paper': {
      position: 'relative',
      padding: '32px',
      width: '100%',
      maxWidth: '456px'
    }
  }
}));

const errorText = 'Это поле обязателно';

export const ExportCustomers = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { userPermissions, currentUsersPermissions } = useSelector(
    (state) => state.userPermissions
  );
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const { values, isValid, isSubmitting, errors, handleChange, setFieldValue, handleSubmit } =
    useFormik({
      initialValues: {
        from_date: '',
        to_date: ''
      },
      validationSchema: yup.object({
        from_date: yup.date().required(errorText),
        to_date: yup.date().required(errorText)
      }),
      onSubmit: async ({ from_date, to_date }) => {
        if (!isSubmitting) return;
        const base64 = await requestExportCustomers({
          from_date: format(from_date, 'yyyy-MM-dd'),
          to_date: format(to_date, 'yyyy-MM-dd')
        });
        const a = document.createElement('a');
        const fileURL = URL.createObjectURL(base64);
        a.href = fileURL;
        a.download = `export_${format(from_date, 'yyyy/MM/dd')}-${format(
          to_date,
          'yyyy/MM/dd'
        )}.xlsx`;
        document.body.appendChild(a);
        a.click();
      }
    });

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const fields = (name) => ({
    name: name,
    value: values[name],
    error: !!errors[name],
    helperText: errors[name],
    onChange: handleChange,
    size: 'small',
    variant: 'outlined'
  });

  const isVisible = visibleBySectionPermission(permissions, [
    PERMISSIONS.SUPPORT.ExportCustomersList.value
  ]);

  if (!isVisible) {
    return <></>;
  }

  return (
    <>
      <CustomButton variant="contained" onClick={onOpen}>
        Выгрузить список
      </CustomButton>
      <StyledDialog open={isOpen}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gap: '24px' }}>
            <Typography variant="h2">Выгрузить отчет</Typography>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[600]
              }}>
              <CloseIcon />
            </IconButton>

            <Typography>
              Выберите период за который вы хотите сформировать отчет по движению Промокодов
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px'
              }}>
              <CustomDatePicker
                {...fields('from_date')}
                setFieldValue={setFieldValue}
                offLastDate={false}
                placeholder="с"
              />
              <CustomDatePicker
                {...fields('to_date')}
                setFieldValue={setFieldValue}
                offLastDate={false}
                placeholder="по"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
              <CustomButton type="submit" variant="contained" disabled={!isValid}>
                Скачать
              </CustomButton>
            </Box>
          </Box>
        </form>
      </StyledDialog>
    </>
  );
};
